import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import BottomTabNavigation from '../../components/navigation/BottomTabNavigation';
import UserProfileHeader from '../../components/navigation/UserProfileHeader';
import NotificationBadge from '../../components/NotificationBadge';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { hasFeatureAccess, OFFICER_FEATURES } from '../../utils/rolePermissions';

import { supabase } from '../../lib/supabase';
import { logger } from '../../utils/logger';
import { format } from 'date-fns';
import AlertCategoryCard from './components/AlertCategoryCard';
import RealtimeEventStream from './components/RealtimeEventStream';
import NotificationDeliverySettings from './components/NotificationDeliverySettings';
import AlertThresholdConfig from './components/AlertThresholdConfig';
import AlertCorrelationAnalysis from './components/AlertCorrelationAnalysis';
import {
  AlertCategorySkeleton,
  RealtimeEventSkeleton,
  ThresholdConfigSkeleton,
  CorrelationAnalysisSkeleton
} from './components/SkeletonLoader';
import platformEventsService from '../../services/platformEventsService';
import alertManagementService from '../../services/alertManagementService';


const CriticalAlertsDashboard = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [realtimeEnabled, setRealtimeEnabled] = useState(true);
  const [realtimeStatus, setRealtimeStatus] = useState('disconnected');
  const [selectedSeverity, setSelectedSeverity] = useState('all');
  const [selectedEventType, setSelectedEventType] = useState('all');
  const [timeRange, setTimeRange] = useState('24h');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [alertCategories, setAlertCategories] = useState({
    newSignups: { count: 0, velocity: 0, trend: 0, critical: 0 },
    premiumConversions: { count: 0, revenue: 0, trend: 0, critical: 0 },
    featureSpikes: { count: 0, threshold: 0, trend: 0, critical: 0 },
    paymentFailures: { count: 0, retryStatus: 0, trend: 0, critical: 0 },
    systemAnomalies: { count: 0, severity: 0, trend: 0, critical: 0 }
  });
  const [realtimeEvents, setRealtimeEvents] = useState([]);
  const [thresholdConfig, setThresholdConfig] = useState({
    signupVelocity: 50,
    conversionRate: 10,
    featureUsageSpike: 200,
    paymentFailureRate: 5,
    anomalyScore: 7
  });
  const [deliverySettings, setDeliverySettings] = useState({
    email: true,
    sms: false,
    slack: false,
    inApp: true,
    quietHours: { enabled: false, start: '22:00', end: '07:00' }
  });
  const [error, setError] = useState('');
  const [platformEvents, setPlatformEvents] = useState([]);
  const [unacknowledgedCount, setUnacknowledgedCount] = useState(0);
  const [realtimeChannel, setRealtimeChannel] = useState(null);
  const [responseTemplates, setResponseTemplates] = useState([]);
  const [escalationRules, setEscalationRules] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [countdown, setCountdown] = useState(30);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    loadAlertData();
    loadResponseTemplates();
    loadEscalationRules();
    
    if (autoRefresh) {
      const interval = setInterval(() => {
        loadAlertData();
        setCountdown(30);
      }, 30000); // Refresh every 30 seconds
      return () => clearInterval(interval);
    }
  }, [timeRange, autoRefresh]);

  // Countdown timer effect
  useEffect(() => {
    if (autoRefresh && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (autoRefresh && countdown === 0) {
      setCountdown(30);
    }
  }, [countdown, autoRefresh]);

  // Check officer access
  const hasAccess = hasFeatureAccess(userProfile, OFFICER_FEATURES?.CRITICAL_ALERTS);

  // Redirect non-officers
  useEffect(() => {
    if (!loading && userProfile && !hasAccess) {
      navigate('/settings-and-privacy');
    }
  }, [hasAccess, loading, userProfile, navigate]);

  // Real-time platform events subscription
  useEffect(() => {
    if (!hasAccess || !userProfile || !realtimeEnabled) return;

    setRealtimeStatus('connecting');

    // Subscribe to platform events
    const channel = platformEventsService?.subscribeToEvents(
      (event) => {
        setPlatformEvents(prev => [event, ...prev]?.slice(0, 100)); // Keep last 100 events
        
        // Update unacknowledged count
        if (!event?.acknowledged) {
          setUnacknowledgedCount(prev => prev + 1);
        }

        // Update alert categories based on event type
        updateAlertCategories(event);

        setRealtimeStatus('connected');
      },
      {
        eventTypes: ['USER_SIGNUP', 'PREMIUM_CONVERSION', 'PAYMENT_FAILURE', 'SYSTEM_ANOMALY', 'FEATURE_SPIKE']
      }
    );

    setRealtimeChannel(channel);

    // Load initial data
    loadPlatformEvents();
    loadUnacknowledgedCount();

    // Cleanup on unmount
    return () => {
      if (channel) {
        platformEventsService?.unsubscribe(channel);
        setRealtimeStatus('disconnected');
      }
    };
  }, [hasAccess, userProfile, realtimeEnabled]);

  const loadPlatformEvents = async () => {
    try {
      // SIMPLIFIED: Remove platform events loading
      setPlatformEvents([]);
    } catch (err) {
      logger?.error('Failed to load platform events', err);
    }
  };

  const loadUnacknowledgedCount = async () => {
    try {
      const { data, error } = await platformEventsService?.getUnacknowledgedCount();
      if (!error && data !== null) {
        setUnacknowledgedCount(data);
      }
    } catch (err) {
      logger?.error('Failed to load unacknowledged count', err);
    }
  };

  const loadResponseTemplates = async () => {
    try {
      const { data, error } = await alertManagementService?.getAllResponseTemplates();
      if (!error && data) {
        setResponseTemplates(data);
      }
    } catch (err) {
      logger?.error('Failed to load response templates', err);
    }
  };

  const loadEscalationRules = async () => {
    try {
      const { data, error } = await alertManagementService?.getEscalationRules();
      if (!error && data) {
        setEscalationRules(data);
      }
    } catch (err) {
      logger?.error('Failed to load escalation rules', err);
    }
  };

  const loadAlertData = async () => {
    setLoading(true);
    setError('');
    setIsRefreshing(true);

    try {
      // SIMPLIFIED: Load only critical symptom alerts
      await loadCriticalSymptomAlerts();
      
      setLastUpdated(new Date());
    } catch (err) {
      setError('Failed to load alert data');
      logger?.error('Alert dashboard load error', err);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const loadCriticalSymptomAlerts = async () => {
    try {
      // SIMPLIFIED: Get only high severity symptom entries from last 24 hours
      const yesterday = new Date();
      yesterday?.setDate(yesterday?.getDate() - 1);

      const { data: criticalSymptoms, error } = await supabase
        ?.from('symptom_entries')
        ?.select('id, user_id, symptom_category, severity_level, created_at')
        ?.gte('severity_level', 8) // High severity only
        ?.gte('created_at', yesterday?.toISOString())
        ?.order('created_at', { ascending: false })
        ?.limit(50);

      if (error) {
        logger?.error('Failed to load critical symptom alerts', error);
        throw error;
      }

      console.log('Critical Alerts Dashboard - Query Results:', {
        criticalSymptomsCount: criticalSymptoms?.length || 0,
        criticalSymptoms: criticalSymptoms,
        queryDate: yesterday?.toISOString()
      });

      // SIMPLIFIED: Set only critical symptom count
      setAlertCategories({
        newSignups: { count: 0, velocity: 0, trend: 0, critical: 0 }, // Removed
        premiumConversions: { count: 0, revenue: 0, trend: 0, critical: 0 }, // Removed
        featureSpikes: { count: 0, threshold: 0, trend: 0, critical: 0 }, // Removed
        paymentFailures: { count: 0, retryStatus: 0, trend: 0, critical: 0 }, // Removed
        systemAnomalies: { count: criticalSymptoms?.length || 0, severity: 0, trend: 0, critical: criticalSymptoms?.length || 0 } // Only symptom alerts
      });

      // Set realtime events to show critical symptoms
      setRealtimeEvents(criticalSymptoms?.map(symptom => ({
        id: symptom?.id,
        eventType: 'CRITICAL_SYMPTOM',
        eventCategory: 'health',
        eventSeverity: 'critical',
        userId: symptom?.user_id,
        metadata: {
          symptomCategory: symptom?.symptom_category,
          severityLevel: symptom?.severity_level
        },
        createdAt: symptom?.created_at,
        acknowledged: false
      })) || []);
    } catch (err) {
      logger?.error('Failed to load critical symptom alerts', err);
      setAlertCategories({
        newSignups: { count: 0, velocity: 0, trend: 0, critical: 0 },
        premiumConversions: { count: 0, revenue: 0, trend: 0, critical: 0 },
        featureSpikes: { count: 0, threshold: 0, trend: 0, critical: 0 },
        paymentFailures: { count: 0, retryStatus: 0, trend: 0, critical: 0 },
        systemAnomalies: { count: 0, severity: 0, trend: 0, critical: 0 }
      });
    }
  };

  const updateAlertCategories = (event) => {
    // SIMPLIFIED: Remove real-time event processing for non-symptom events
    if (event?.eventType === 'CRITICAL_SYMPTOM') {
      setAlertCategories(prev => ({
        ...prev,
        systemAnomalies: {
          ...prev?.systemAnomalies,
          count: prev?.systemAnomalies?.count + 1,
          critical: prev?.systemAnomalies?.critical + 1
        }
      }));
    }
  };

  const handleAcknowledgeEvent = async (eventId) => {
    try {
      const { error } = await platformEventsService?.acknowledgeEvent(eventId);
      if (!error) {
        setPlatformEvents(prev => 
          prev?.map(event => 
            event?.id === eventId 
              ? { ...event, acknowledged: true, acknowledgedAt: new Date()?.toISOString() }
              : event
          )
        );
        setUnacknowledgedCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      logger?.error('Failed to acknowledge event', err);
    }
  };

  const handleAcknowledgeAll = async () => {
    try {
      const unacknowledgedIds = platformEvents?.filter(event => !event?.acknowledged)?.map(event => event?.id);

      if (unacknowledgedIds?.length === 0) return;

      const { error } = await platformEventsService?.acknowledgeMultipleEvents(unacknowledgedIds);
      if (!error) {
        setPlatformEvents(prev => 
          prev?.map(event => ({ ...event, acknowledged: true, acknowledgedAt: new Date()?.toISOString() }))
        );
        setUnacknowledgedCount(0);
      }
    } catch (err) {
      logger?.error('Failed to acknowledge all events', err);
    }
  };

  const handleNotificationClick = () => {
    // Scroll to events section
    document.getElementById('realtime-events')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleRealtimeEvent = (eventType, payload) => {
    const event = {
      id: payload?.new?.id,
      type: eventType,
      severity: calculateSeverity(eventType, payload?.new),
      timestamp: new Date()?.toISOString(),
      data: payload?.new,
      acknowledged: false
    };

    setRealtimeEvents(prev => [event, ...prev]?.slice(0, 100));
    
    // Update category counts
    updateCategoryCounts(eventType);
    
    // Check if notification should be sent
    if (shouldSendNotification(event)) {
      sendNotification(event);
    }
  };

  const calculateSeverity = (eventType, data) => {
    switch (eventType) {
      case 'PAYMENT_FAILURE':
        return 'Critical';
      case 'SYSTEM_ANOMALY':
        return data?.severity >= 8 ? 'Critical' : data?.severity >= 5 ? 'High' : 'Medium';
      case 'PREMIUM_CONVERSION':
        return 'Low';
      case 'NEW_SIGNUP':
        return 'Low';
      default:
        return 'Medium';
    }
  };

  const shouldSendNotification = (event) => {
    // Check quiet hours
    if (deliverySettings?.quietHours?.enabled) {
      const now = new Date();
      const currentTime = format(now, 'HH:mm');
      if (currentTime >= deliverySettings?.quietHours?.start || 
          currentTime <= deliverySettings?.quietHours?.end) {
        return false;
      }
    }
    
    // Only send for Critical and High severity
    return event?.severity === 'Critical' || event?.severity === 'High';
  };

  const sendNotification = async (event) => {
    try {
      if (deliverySettings?.email) {
        // Send email notification (would integrate with edge function)
        logger?.info('Email notification sent', { eventType: event?.type });
      }
      if (deliverySettings?.inApp) {
        // Show in-app notification
        showInAppNotification(event);
      }
    } catch (error) {
      logger?.error('Failed to send notification', error);
    }
  };

  const showInAppNotification = (event) => {
    // Browser notification if permitted
    if ('Notification' in window && Notification?.permission === 'granted') {
      new Notification(`Critical Alert: ${event?.type}`, {
        body: `Severity: ${event?.severity}`,
        icon: '/favicon.ico'
      });
    }
  };

  const updateCategoryCounts = (eventType) => {
    setAlertCategories(prev => {
      const updated = { ...prev };
      switch (eventType) {
        case 'NEW_SIGNUP':
          updated.newSignups.count += 1;
          break;
        case 'PREMIUM_CONVERSION':
          updated.premiumConversions.count += 1;
          break;
        case 'PAYMENT_FAILURE':
          updated.paymentFailures.count += 1;
          updated.paymentFailures.critical += 1;
          break;
        case 'SYSTEM_ANOMALY':
          updated.systemAnomalies.count += 1;
          break;
      }
      return updated;
    });
  };

  const handleAcknowledgeAlert = (alertId) => {
    setRealtimeEvents(prev => 
      prev?.map(event => 
        event?.id === alertId ? { ...event, acknowledged: true } : event
      )
    );
  };

  const handleThresholdUpdate = (thresholds) => {
    setThresholdConfig(thresholds);
    logger?.audit('ALERT_THRESHOLDS_UPDATED', user?.id, { thresholds });
  };

  const handleDeliverySettingsUpdate = (settings) => {
    setDeliverySettings(settings);
    logger?.audit('NOTIFICATION_DELIVERY_UPDATED', user?.id, { settings });
  };

  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    setCountdown(30);
    await loadAlertData();
    setTimeout(() => {
      setIsRefreshing(false);
    }, 500);
  };

  const handleExport = async () => {
    try {
      const csvContent = [
        ['Timestamp', 'Event Type', 'Severity', 'Acknowledged'],
        ...realtimeEvents?.map(event => [
          format(new Date(event?.timestamp), 'yyyy-MM-dd HH:mm:ss'),
          event?.type,
          event?.severity,
          event?.acknowledged ? 'Yes' : 'No'
        ])
      ]?.map(row => row?.join(','))?.join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL?.createObjectURL(blob);
      const link = document?.createElement('a');
      link.href = url;
      link.download = `critical-alerts-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      document?.body?.appendChild(link);
      link?.click();
      document?.body?.removeChild(link);
      URL?.revokeObjectURL(url);
    } catch (error) {
      logger?.error('Export failed', error);
    }
  };

  const toggleRealtime = () => {
    setRealtimeEnabled(prev => !prev);
  };

  const getRealtimeStatusColor = () => {
    switch (realtimeStatus) {
      case 'connected': return 'text-success';
      case 'connecting': return 'text-warning';
      case 'error': return 'text-destructive';
      default: return 'text-muted-foreground';
    }
  };

  const filteredEvents = realtimeEvents?.filter(event => {
    if (selectedSeverity !== 'all' && event?.severity !== selectedSeverity) return false;
    if (selectedEventType !== 'all' && event?.type !== selectedEventType) return false;
    return true;
  });

  const totalCriticalAlerts = Object.values(alertCategories)?.reduce((sum, cat) => sum + cat?.critical, 0);

  const handleSnoozeAlert = async (eventId, hours = 1) => {
    try {
      const snoozeUntil = new Date();
      snoozeUntil?.setHours(snoozeUntil?.getHours() + hours);

      const { error } = await alertManagementService?.snoozeAlert(
        eventId,
        snoozeUntil,
        `Snoozed for ${hours} hour(s)`
      );

      if (!error) {
        setPlatformEvents(prev =>
          prev?.map(event =>
            event?.id === eventId
              ? { ...event, snoozed: true, snoozedUntil: snoozeUntil?.toISOString() }
              : event
          )
        );
        logger?.audit('ALERT_SNOOZED', user?.id, { eventId, hours });
      }
    } catch (err) {
      logger?.error('Failed to snooze alert', err);
    }
  };

  const handleRouteAlert = async (eventId, routeToUserId) => {
    try {
      const { error } = await alertManagementService?.routeAlert(
        eventId,
        routeToUserId,
        'Routed to another officer'
      );

      if (!error) {
        setPlatformEvents(prev =>
          prev?.map(event =>
            event?.id === eventId
              ? { ...event, routed: true, routedTo: routeToUserId }
              : event
          )
        );
        logger?.audit('ALERT_ROUTED', user?.id, { eventId, routeToUserId });
      }
    } catch (err) {
      logger?.error('Failed to route alert', err);
    }
  };

  const handleEscalateAlert = async (eventId) => {
    try {
      const { error } = await alertManagementService?.escalateAlert(
        eventId,
        'Escalated due to severity or time threshold'
      );

      if (!error) {
        setPlatformEvents(prev =>
          prev?.map(event =>
            event?.id === eventId
              ? { ...event, escalated: true }
              : event
          )
        );
        logger?.audit('ALERT_ESCALATED', user?.id, { eventId });
      }
    } catch (err) {
      logger?.error('Failed to escalate alert', err);
    }
  };

  const getResponseTemplate = (eventType, severity) => {
    return responseTemplates?.find(
      template => template?.event_type === eventType && template?.severity === severity
    );
  };

  const openActionModal = (event, action) => {
    setSelectedEvent(event);
    setActionType(action);
    setShowActionModal(true);
  };

  return (
    <>
      <Helmet>
        <title>Critical Alerts Dashboard | MyBodyToday Officers</title>
        <meta name="description" content="Monitor and manage critical platform alerts in real-time" />
      </Helmet>
      <UserProfileHeader />
      <div className="min-h-screen bg-background pb-24">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-2.5 py-2 sm:px-3 sm:py-2.5 md:px-4 md:py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate(-1)}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                  aria-label="Go back"
                >
                  <Icon name="ArrowLeft" className="w-4 h-4 text-gray-600" />
                </button>
                <div>
                  <h1 className="text-base sm:text-lg md:text-xl font-bold text-gray-900">Critical Alerts</h1>
                  <p className="text-xs sm:text-sm md:text-base text-gray-500">Real-time monitoring & response</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <NotificationBadge
                  count={unacknowledgedCount}
                  severity={unacknowledgedCount > 10 ? 'critical' : unacknowledgedCount > 5 ? 'warning' : 'info'}
                  onClick={handleNotificationClick}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-2.5 py-3 sm:px-3 sm:py-4 md:px-4 md:py-6 space-y-4 sm:space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <Icon name="AlertCircle" size={20} strokeWidth={2} className="text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-900">Error Loading Alerts</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          )}

          {loading ? (
            <>
              {/* Alert Categories Skeleton */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <AlertCategorySkeleton />
                <AlertCategorySkeleton />
                <AlertCategorySkeleton />
                <AlertCategorySkeleton />
                <AlertCategorySkeleton />
              </div>

              {/* Realtime Event Stream Skeleton */}
              <RealtimeEventSkeleton />

              {/* Threshold Config Skeleton */}
              <ThresholdConfigSkeleton />

              {/* Correlation Analysis Skeleton */}
              <CorrelationAnalysisSkeleton />
            </>
          ) : (
            <>
              {/* Alert Category Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <AlertCategoryCard
                  title="New User Signups"
                  icon="UserPlus"
                  count={alertCategories?.newSignups?.count}
                  metric={`${alertCategories?.newSignups?.velocity}/day velocity`}
                  trend={alertCategories?.newSignups?.trend}
                  critical={alertCategories?.newSignups?.critical}
                  color="primary"
                />
                <AlertCategoryCard
                  title="Premium Conversions"
                  icon="TrendingUp"
                  count={alertCategories?.premiumConversions?.count}
                  metric={`$${alertCategories?.premiumConversions?.revenue?.toFixed(2)} revenue`}
                  trend={alertCategories?.premiumConversions?.trend}
                  critical={alertCategories?.premiumConversions?.critical}
                  color="success"
                />
                <AlertCategoryCard
                  title="Feature Usage Spikes"
                  icon="Activity"
                  count={alertCategories?.featureSpikes?.count}
                  metric={`Threshold: ${alertCategories?.featureSpikes?.threshold}`}
                  trend={alertCategories?.featureSpikes?.trend}
                  critical={alertCategories?.featureSpikes?.critical}
                  color="warning"
                />
                <AlertCategoryCard
                  title="Payment Failures"
                  icon="CreditCard"
                  count={alertCategories?.paymentFailures?.count}
                  metric={`${alertCategories?.paymentFailures?.retryStatus} retry pending`}
                  trend={alertCategories?.paymentFailures?.trend}
                  critical={alertCategories?.paymentFailures?.critical}
                  color="destructive"
                />
                <AlertCategoryCard
                  title="System Anomalies"
                  icon="AlertOctagon"
                  count={alertCategories?.systemAnomalies?.count}
                  metric={`Severity: ${alertCategories?.systemAnomalies?.severity}/10`}
                  trend={alertCategories?.systemAnomalies?.trend}
                  critical={alertCategories?.systemAnomalies?.critical}
                  color="destructive"
                />
              </div>

              {/* Real-time Event Stream */}
              <RealtimeEventStream
                events={filteredEvents}
                onAcknowledge={handleAcknowledgeAlert}
              />

              {/* Configuration Panels */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <AlertThresholdConfig
                  thresholds={thresholdConfig}
                  onUpdate={handleThresholdUpdate}
                />
                <NotificationDeliverySettings
                  settings={deliverySettings}
                  onUpdate={handleDeliverySettingsUpdate}
                />
              </div>

              {/* Alert Correlation Analysis */}
              <AlertCorrelationAnalysis
                events={realtimeEvents}
                categories={alertCategories}
              />
            </>
          )}
        </div>

        {/* Action Modal */}
        {showActionModal && selectedEvent && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900">
                    {actionType === 'snooze' && 'Snooze Alert'}
                    {actionType === 'route' && 'Route Alert'}
                    {actionType === 'escalate' && 'Escalate Alert'}
                    {actionType === 'template' && 'Response Template'}
                  </h3>
                  <button
                    onClick={() => setShowActionModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <Icon name="X" size={24} />
                  </button>
                </div>

                {/* Event Details */}
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      selectedEvent?.eventSeverity === 'critical' ? 'bg-red-100 text-red-700' :
                      selectedEvent?.eventSeverity === 'warning'? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {selectedEvent?.eventSeverity?.toUpperCase()}
                    </span>
                    <span className="text-sm text-gray-600">{selectedEvent?.eventType}</span>
                  </div>
                  <p className="text-sm text-gray-700">
                    {selectedEvent?.eventCategory}
                  </p>
                </div>

                {/* Response Template */}
                {actionType === 'template' && (
                  <div className="space-y-4">
                    {(() => {
                      const template = getResponseTemplate(selectedEvent?.eventType, selectedEvent?.eventSeverity);
                      if (!template) {
                        return (
                          <p className="text-sm text-gray-500">No response template available for this alert type.</p>
                        );
                      }
                      return (
                        <>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900 mb-2">{template?.template_name}</h4>
                            <p className="text-sm text-gray-700 mb-4">{template?.template_content}</p>
                          </div>
                          {template?.action_steps && template?.action_steps?.length > 0 && (
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900 mb-2">Action Steps:</h4>
                              <ol className="list-decimal list-inside space-y-2">
                                {template?.action_steps?.map((step, index) => (
                                  <li key={index} className="text-sm text-gray-700">{step}</li>
                                ))}
                              </ol>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Snooze Options */}
                {actionType === 'snooze' && (
                  <div className="space-y-3">
                    <button
                      onClick={() => {
                        handleSnoozeAlert(selectedEvent?.id, 1);
                        setShowActionModal(false);
                      }}
                      className="w-full px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-lg text-left transition-colors"
                    >
                      <span className="font-medium">1 Hour</span>
                    </button>
                    <button
                      onClick={() => {
                        handleSnoozeAlert(selectedEvent?.id, 4);
                        setShowActionModal(false);
                      }}
                      className="w-full px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-lg text-left transition-colors"
                    >
                      <span className="font-medium">4 Hours</span>
                    </button>
                    <button
                      onClick={() => {
                        handleSnoozeAlert(selectedEvent?.id, 24);
                        setShowActionModal(false);
                      }}
                      className="w-full px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-lg text-left transition-colors"
                    >
                      <span className="font-medium">24 Hours</span>
                    </button>
                  </div>
                )}

                {/* Escalate Confirmation */}
                {actionType === 'escalate' && (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-700">
                      This will escalate the alert to senior officers and trigger additional notifications.
                    </p>
                    {escalationRules?.find(rule => 
                      rule?.event_type === selectedEvent?.eventType && 
                      rule?.severity === selectedEvent?.eventSeverity
                    ) && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <p className="text-sm text-yellow-800">
                          <strong>Escalation Rule Active:</strong> This alert will be escalated according to configured rules.
                        </p>
                      </div>
                    )}
                    <div className="flex gap-3">
                      <button
                        onClick={() => setShowActionModal(false)}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => {
                          handleEscalateAlert(selectedEvent?.id);
                          setShowActionModal(false);
                        }}
                        className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                      >
                        Escalate Now
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Real-time Events Section */}
        <div id="realtime-events" className="px-6 py-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Live Event Stream</h2>
            {unacknowledgedCount > 0 && (
              <button
                onClick={handleAcknowledgeAll}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
              >
                Acknowledge All ({unacknowledgedCount})
              </button>
            )}
          </div>
          
          {/* Event Cards with Actions */}
          <div className="space-y-4">
            {platformEvents?.slice(0, 20)?.map(event => (
              <div key={event?.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        event?.eventSeverity === 'critical' ? 'bg-red-100 text-red-700' :
                        event?.eventSeverity === 'warning'? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {event?.eventSeverity?.toUpperCase()}
                      </span>
                      <span className="text-sm font-medium text-gray-900">{event?.eventType}</span>
                      {event?.acknowledged && (
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                          Acknowledged
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{event?.eventCategory}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(event?.createdAt)?.toLocaleString()}
                    </p>
                  </div>
                  
                  {/* Action Buttons */}
                  {!event?.acknowledged && (
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => handleAcknowledgeEvent(event?.id)}
                        className="px-3 py-1.5 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700 transition-colors"
                        title="Acknowledge"
                      >
                        <Icon name="Check" size={14} />
                      </button>
                      <button
                        onClick={() => openActionModal(event, 'snooze')}
                        className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 transition-colors"
                        title="Snooze"
                      >
                        <Icon name="Clock" size={14} />
                      </button>
                      <button
                        onClick={() => openActionModal(event, 'template')}
                        className="px-3 py-1.5 bg-purple-600 text-white rounded text-xs font-medium hover:bg-purple-700 transition-colors"
                        title="View Response Template"
                      >
                        <Icon name="FileText" size={14} />
                      </button>
                      <button
                        onClick={() => openActionModal(event, 'escalate')}
                        className="px-3 py-1.5 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 transition-colors"
                        title="Escalate"
                      >
                        <Icon name="AlertTriangle" size={14} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <BottomTabNavigation />
    </>
  );
};

export default CriticalAlertsDashboard;